import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useAxiosHook, {
  UseAxiosReturn,
} from '../../../../../../../../hooks/useAxiosHook';
import useTableColumns from '../../../../../../../../hooks/useTableColumns';
import useTableState from '../../../../../../../../hooks/useTableState';
import { InvoiceShipmentCollection } from '../../../../../../../../types/api/invoices';
import { Unpacked } from '../../../../../../../../types/util';
import Table from '../../../../../../../DataTable/Table/Table';
import ViewActiveOrderDialog from '../../../../../../Orders/Dialogs/View/ViewActiveOrderDialog';
import { SingleOrder } from '../../../../../../Orders/Orders.functions';
import {
  additionalColumnProperties,
  generateContextMenu,
  getColumnHeadersMap,
  tableStorageKey,
} from './Shipments.functions';

type Props = {
  request: UseAxiosReturn<InvoiceShipmentCollection>;
};

function Shipments({ request }: Props): JSX.Element {
  const { t } = useTranslation();

  const [isViewOrderDialogVisible, setIsViewOrderDialogVisible] =
    useState(false);

  const columnHeadersMap = useMemo(() => getColumnHeadersMap(t), [t]);

  const {
    tableRef,
    page,
    setPage,
    limit,
    setLimit,
    sortField,
    setSortField,
    sortOrder,
    setSortOrder,
    selection,
    setSelection,
  } = useTableState<Unpacked<InvoiceShipmentCollection>>(tableStorageKey);

  const { columns, columnOptions, selectedColumns, setSelectedColumns } =
    useTableColumns(
      page,
      limit,
      'invoiceViewShipments',
      columnHeadersMap,
      columnHeadersMap,
      additionalColumnProperties
    );

  const { data, error, isLoading, reload } = request;

  const { data: orderData, isLoading: isOrderDataLoading } =
    useAxiosHook<SingleOrder>(
      {
        url: `/orders/${selection?.adresnica_id}`,
      },
      {
        skipWhen: !selection?.adresnica_id || !isViewOrderDialogVisible,
      }
    );

  const headerTitle = t('Shipments');

  const contextMenuModel = useMemo(
    () => generateContextMenu(t, () => setIsViewOrderDialogVisible(true)),
    [t]
  );

  return (
    <>
      <ViewActiveOrderDialog
        data={orderData}
        isLoading={isOrderDataLoading}
        onHide={() => setIsViewOrderDialogVisible(false)}
        visible={isViewOrderDialogVisible}
      />

      <Table
        tableRef={tableRef}
        columnOptions={columnOptions}
        columns={columns}
        data={data}
        hasError={!!error}
        headerTitle={headerTitle}
        isLoading={isLoading}
        rebuildTooltip
        reload={reload}
        rows={limit}
        selectedColumns={selectedColumns}
        selection={selection}
        setLimit={setLimit}
        setPage={setPage}
        setSelectedColumns={setSelectedColumns}
        setSelection={setSelection}
        setSortField={setSortField}
        setSortOrder={setSortOrder}
        sortField={sortField}
        sortOrder={sortOrder}
        storageString={tableStorageKey}
        contextMenuModel={contextMenuModel}
        onRowDoubleClick={() => setIsViewOrderDialogVisible(true)}
      />
    </>
  );
}

export default Shipments;
